import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fileToCompressedDataUrl, uploadCompressedPhoto } from "@/lib/imageUpload";
import { formatPhotoCaption } from "@/lib/photoCaption";
import AuthImage from "@/components/AuthImage";
import { isEmployeeTier, isManagerTier } from "@/lib/roles";
import { ArrowLeft, Camera, Trash2, ChevronRight, CheckCircle2, AlertTriangle, Send, Search, X, Users, Sparkles as Broom } from "lucide-react";

const CONDITIONS = [
  { key: "excellent", label: "Excellent", color: "emerald" },
  { key: "good", label: "Good", color: "gold" },
  { key: "needs_attention", label: "Needs Attention", color: "warning" },
  { key: "critical", label: "Critical", color: "error" },
];

const CLEANLINESS = [
  { key: "very_clean", label: "Very Clean", color: "emerald" },
  { key: "clean", label: "Clean", color: "emerald" },
  { key: "needs_cleaning", label: "Needs Cleaning", color: "warning" },
  { key: "dust_present", label: "Dust Present", color: "warning" },
  { key: "dirty", label: "Dirty", color: "error" },
  { key: "critical", label: "Critical Cleaning", color: "error" },
];

const DUST = [
  { key: "none", label: "No Visible Dust" },
  { key: "light", label: "Light Dust" },
  { key: "moderate", label: "Moderate Dust" },
  { key: "heavy", label: "Heavy Dust" },
];

const CLEAN_CHECKLIST = [
  { key: "floor_clean", label: "Is the floor clean?", allowNA: false },
  { key: "shelves_dust_free", label: "Are shelves free of dust?", allowNA: false },
  { key: "products_dust_free", label: "Are products free of dust?", allowNA: false },
  { key: "glass_clean", label: "Is display glass/mirror clean?", allowNA: true },
  { key: "no_visible_trash", label: "Is there NO visible trash or packaging?", allowNA: false },
  { key: "corners_clean", label: "Are corners/hidden areas clean?", allowNA: false },
  { key: "fixtures_clean", label: "Are display fixtures clean?", allowNA: false },
  { key: "no_spills", label: "Is there NO visible spills or stains?", allowNA: false },
];

const CHECKLIST_ITEMS = [
  { key: "stocked", label: "Is the display fully stocked?" },
  { key: "arranged", label: "Are products correctly arranged?" },
  { key: "prices_visible", label: "Are price labels visible?" },
  { key: "no_empty_spaces", label: "No empty display spaces?" },
  { key: "correct_fandom_area", label: "Products in the correct fandom area?" },
];

const ISSUE_TYPES = [
  { key: "low_stock", label: "Low Stock" },
  { key: "out_of_stock", label: "Out of Stock" },
  { key: "incorrect_placement", label: "Incorrect Product Placement" },
  { key: "empty_shelf", label: "Empty Shelf" },
  { key: "missing_price_label", label: "Missing Price Label" },
  { key: "damaged_product", label: "Damaged Product" },
  { key: "damaged_display", label: "Damaged Display" },
  { key: "display_not_clean", label: "Display Not Clean" },
  { key: "dust_present", label: "Dust Present" },
  { key: "incorrect_fandom_placement", label: "Incorrect Fandom Placement" },
  { key: "promo_missing", label: "Promotional Material Missing" },
  { key: "other", label: "Other" },
];

const ISSUE_LABEL = Object.fromEntries(ISSUE_TYPES.map((i) => [i.key, i.label]));

export default function DisplayRun() {
  const { id } = useParams();
  const nav = useNavigate();
  const [dc, setDc] = useState(null);
  const [zoneIdx, setZoneIdx] = useState(0);
  const [settings, setSettings] = useState({ min_photos_per_zone: 1, max_photos_per_zone: 30, require_zone_owner: true });
  const [fandoms, setFandoms] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allStandards, setAllStandards] = useState([]);
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reviewing, setReviewing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, s, f, e, st] = await Promise.all([
        api.get(`/display_checks/${id}`), api.get("/settings"), api.get("/fandoms"), api.get("/employees"),
        api.get("/display_standards", { params: { active_only: true } }),
      ]);
      setDc(d.data);
      setSettings({
        min_photos_per_zone: Number(s.data?.min_photos_per_zone) || 1,
        max_photos_per_zone: Number(s.data?.max_photos_per_zone) || 30,
        require_zone_owner: s.data?.require_zone_owner !== false,
        store_name: s.data?.store_name || "",
      });
      setFandoms(f.data);
      setEmployees(e.data);
      setAllStandards(st.data);
      if (["assigned", "correction_required"].includes(d.data.status)) {
        await api.post(`/display_checks/${id}/start`, {});
        const again = await api.get(`/display_checks/${id}`);
        setDc(again.data);
      }
      const zones = d.data.zones || [];
      const first = zones.findIndex((z) => !["completed", "corrected"].includes(z.status));
      if (first >= 0) setZoneIdx(first);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!dc) return <div className="text-[color:var(--text-secondary)]">Loading…</div>;

  const zones = dc.zones || [];
  const totalZones = zones.length;
  const completedCount = zones.filter((z) => ["completed", "corrected"].includes(z.status)).length;
  const progressPct = totalZones ? Math.round(100 * completedCount / totalZones) : 0;
  const currentZone = zones[zoneIdx];

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="dc-run">
      <button onClick={() => nav(-1)} className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)]"><ArrowLeft size={14}/> Back</button>

      <div>
        <div className="label-caps">Display Check</div>
        <h1 className="font-heading text-2xl sm:text-3xl">{dc.check_number}</h1>
        <div className="text-xs text-[color:var(--text-secondary)] mt-1">{dc.store_name} · {dc.inspection_date}</div>
      </div>

      <div className="hos-card p-3">
        <div className="flex items-center justify-between mb-2 text-sm">
          <span className="font-semibold">{completedCount} of {totalZones} Zones</span>
          <span className="text-[color:var(--gold)]">{progressPct}%</span>
        </div>
        <div className="h-2 bg-[color:var(--bg-surface-2)] rounded-full overflow-hidden">
          <div className="h-full bg-[color:var(--gold)] transition-all" style={{ width: `${progressPct}%` }}/>
        </div>
      </div>

      {dc.instructions && (
        <div className="hos-card p-3 text-sm border border-[color:var(--gold)]/40 bg-[color:var(--gold)]/5">
          <div className="label-caps mb-1">Instructions</div>{dc.instructions}
        </div>
      )}

      {reviewing ? (
        <SummaryView dc={dc} onCancel={() => setReviewing(false)} onSubmit={async () => {
          setSubmitting(true);
          try {
            await api.post(`/display_checks/${id}/submit`, {});
            toast.success("Display Check submitted for GM review");
            nav("/display");
          } catch (e) { toast.error(formatApiError(e)); }
          finally { setSubmitting(false); }
        }} submitting={submitting}/>
      ) : completedCount === totalZones ? (
        <div className="hos-card p-4 text-center space-y-3">
          <CheckCircle2 size={40} className="text-emerald-400 mx-auto"/>
          <div className="font-heading text-2xl">All zones inspected!</div>
          <div className="text-sm text-[color:var(--text-secondary)]">Review the summary and submit for GM approval.</div>
          <button className="hos-btn-primary w-full" onClick={() => setReviewing(true)} data-testid="dc-review-btn">Review & Submit</button>
        </div>
      ) : (
        <ZoneFlow
          key={currentZone.zone_id + (currentZone.status || "")}
          zone={currentZone}
          totalZones={totalZones}
          zoneIdx={zoneIdx}
          settings={settings}
          fandoms={fandoms}
          employees={employees}
          allStandards={allStandards}
          onSave={async (payload) => {
            setBusy(true);
            try {
              await api.put(`/display_checks/${id}/zones/${currentZone.zone_id}`, payload);
              toast.success(`Zone approved: ${currentZone.zone_name}`);
              const fresh = await api.get(`/display_checks/${id}`);
              setDc(fresh.data);
              const zonesNext = fresh.data.zones || [];
              let nextIdx = zonesNext.findIndex((z, i) => i > zoneIdx && !["completed", "corrected"].includes(z.status));
              if (nextIdx < 0) nextIdx = zonesNext.findIndex((z) => !["completed", "corrected"].includes(z.status));
              if (nextIdx >= 0) {
                setZoneIdx(nextIdx);
                toast.info(`Next: ${zonesNext[nextIdx].zone_name}`);
              }
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setBusy(false); }
          }}
          onJump={(i) => setZoneIdx(i)}
          zones={zones}
          busy={busy}
        />
      )}
    </div>
  );
}

function ZoneFlow({ zone, zoneIdx, totalZones, settings, fandoms, employees, allStandards, onSave, onJump, zones, busy }) {
  const isCorrection = zone.status === "correction_required";
  const [fandomIds, setFandomIds] = useState(zone.fandom_ids || []);
  const [condition, setCondition] = useState(zone.condition || "");
  const [checklist, setChecklist] = useState(zone.checklist || {});
  const [issues, setIssues] = useState(zone.issues || []);
  const [otherDetail, setOtherDetail] = useState(zone.other_issue_detail || "");
  const [comments, setComments] = useState(zone.comments || "");
  const [actionTaken, setActionTaken] = useState(zone.action_taken || "");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  // New: zone owners, cleanliness, issue photos
  const [ownerIds, setOwnerIds] = useState(zone.zone_owner_ids || []);
  const [cleanStatus, setCleanStatus] = useState(zone.cleanliness_status || "");
  const [dust, setDust] = useState(zone.dust_condition || "");
  const [cleanCk, setCleanCk] = useState(zone.cleanliness_checklist || {});
  const [cleanNotes, setCleanNotes] = useState(zone.cleanliness_notes || "");
  const [issuePhotos, setIssuePhotos] = useState({}); // { issue_key: [{data_url,...}] }
  const [standardScores, setStandardScores] = useState({}); // { standard_id: percent }
  const [displayAreas, setDisplayAreas] = useState([]);
  const [selectedAreaId, setSelectedAreaId] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get(`/display_areas?zone_id=${zone.zone_id}&active_only=true`).then(({ data }) => {
      if (cancelled) return;
      setDisplayAreas(data);
      // Preserve current selection if it's still valid; otherwise pick the first area.
      setSelectedAreaId((prev) => {
        if (prev && data.some((a) => a.id === prev)) return prev;
        return data[0]?.id || "";
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [zone.zone_id]);

  const areaCoverage = useMemo(() => {
    const map = new Map();
    for (const a of displayAreas) map.set(a.id, { area: a, count: 0 });
    for (const p of photos) {
      if (p.display_area_id && map.has(p.display_area_id)) map.get(p.display_area_id).count += 1;
    }
    return [...map.values()];
  }, [displayAreas, photos]);

  // Filter standards for this zone/fandom.  Prefer fandom-specific set; fall back to zone-level.
  const activeStandards = useMemo(() => {
    const picked = fandomIds[0] || null;
    const zoneAll = (allStandards || []).filter((s) => s.zone_id === zone.zone_id && s.active !== false);
    if (picked) {
      const specific = zoneAll.filter((s) => s.fandom_id === picked);
      if (specific.length) return specific.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    }
    return zoneAll.filter((s) => !s.fandom_id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [allStandards, zone.zone_id, fandomIds]);

  const stdTotal = activeStandards.reduce((s, x) => s + Number(x.weight_percentage || 0), 0);
  const stdWeightsValid = activeStandards.length > 0 && Math.abs(stdTotal - 100) < 0.5;

  // For auto_cleanliness standards, derive the score from cleanliness_status
  const cleanlinessMap = { very_clean: 100, clean: 90, needs_cleaning: 60, dust_present: 55, dirty: 30, critical: 10 };
  const effectiveScoreFor = (st) => {
    if (st.scoring_type === "auto_cleanliness") return cleanlinessMap[cleanStatus] ?? null;
    const v = standardScores[st.id];
    return v === undefined ? null : Number(v);
  };
  const previewScore = () => {
    if (!activeStandards.length || !stdWeightsValid) return null;
    let acc = 0;
    for (const st of activeStandards) {
      const sc = effectiveScoreFor(st);
      if (sc === null) return null;
      acc += sc * Number(st.weight_percentage || 0) / 100;
    }
    return Math.round(acc * 100) / 100;
  };

  const availableFandoms = useMemo(() => {
    const availIds = new Set(zone.fandom_ids_available || []);
    const availFromZone = fandoms.filter((f) => availIds.has(f.id));
    return availFromZone.length ? availFromZone : fandoms;
  }, [zone.fandom_ids_available, fandoms]);

  const eligibleEmployees = useMemo(() => (employees || []).filter((u) => u.active !== false && !isEmployeeTier(u.role) === false || isEmployeeTier(u.role) || isManagerTier(u.role)), [employees]);
  // Simpler: any active user visible in /api/employees is eligible.
  const employeePool = useMemo(() => (employees || []).filter((u) => u.active !== false), [employees]);

  const toggleFandom = (id) => setFandomIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleIssue = (k) => setIssues((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
  const setCk = (k, v) => setChecklist({ ...checklist, [k]: v });
  const setCleanItem = (k, v) => setCleanCk({ ...cleanCk, [k]: v });
  const toggleOwner = (uid) => setOwnerIds((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);

  const compressAndAppend = async (fileList, onAppend, opts = {}) => {
    setUploading(true);
    let ok = 0;
    const failures = [];
    const area = displayAreas.find((a) => a.id === selectedAreaId);
    const photoType = opts.photoType || (isCorrection ? "CORRECTION" : "INSPECTION");
    for (const file of fileList) {
      const capturedAt = new Date().toISOString();
      try {
        const r = await uploadCompressedPhoto(file);
        const meta = area
          ? {
              display_area_id: area.id,
              display_area_code: area.code,
              display_area_name: area.name,
              display_area_code_snapshot: area.code,
              display_area_name_snapshot: area.name,
            }
          : {};
        onAppend({
          id: r.id,
          url: r.url,
          preview: r.preview,
          taken_at: capturedAt,
          captured_at: capturedAt,
          zone_name_snapshot: zone.zone_name || "",
          store_name_snapshot: settings.store_name || "",
          photo_type: photoType,
          ...meta,
        });
        ok += 1;
      } catch (err) {
        // Surface the ACTUAL error so we can diagnose upload failures.
        // Include status + response body when it comes from axios.
        const status = err?.response?.status;
        const body = err?.response?.data;
        const bodyStr = typeof body === "string" ? body : body ? JSON.stringify(body).slice(0, 160) : "";
        const detail = status ? `HTTP ${status}${bodyStr ? ` · ${bodyStr}` : ""}` : (err?.message || String(err));
        console.error("[photo upload failed]", file?.name, file?.type, file?.size, err);
        failures.push({ name: file?.name || "photo", detail });
      }
    }
    setUploading(false);
    if (failures.length) {
      const msg = failures.length === 1
        ? `Upload failed — ${failures[0].detail}`
        : `${failures.length} photos failed — ${failures[0].detail}`;
      toast.error(msg, { duration: 12000 });
    }
    if (ok) toast.success(`${ok} photo(s) uploaded`);
  };

  const addPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (displayAreas.length > 0 && !selectedAreaId) {
      toast.error("Select a Display Area before taking photos");
      e.target.value = "";
      return;
    }
    const remain = settings.max_photos_per_zone - photos.length;
    if (remain <= 0) { toast.error(`Max ${settings.max_photos_per_zone} photos per zone`); e.target.value = ""; return; }
    if (files.length > remain) toast.info(`Only ${remain} more photo(s) allowed`);
    await compressAndAppend(files.slice(0, remain), (p) => setPhotos((prev) => [...prev, p]));
    e.target.value = "";
  };
  const removePhoto = (i) => setPhotos((prev) => prev.filter((_, idx) => idx !== i));

  const addIssuePhotos = async (issueKey, files) => {
    const existing = issuePhotos[issueKey] || [];
    const remain = settings.max_photos_per_zone - existing.length;
    if (remain <= 0) { toast.error(`Max ${settings.max_photos_per_zone} photos on this issue`); return; }
    await compressAndAppend(
      files.slice(0, remain),
      (p) => setIssuePhotos((prev) => ({ ...prev, [issueKey]: [...(prev[issueKey] || []), p] })),
      { photoType: "ISSUE" },
    );
  };
  const removeIssuePhoto = (issueKey, i) => setIssuePhotos((prev) => ({ ...prev, [issueKey]: (prev[issueKey] || []).filter((_, idx) => idx !== i) }));

  const missingFields = () => {
    const missing = [];
    if (settings.require_zone_owner && ownerIds.length === 0) missing.push("Select at least one Zone Owner");
    if (fandomIds.length === 0) missing.push("Select at least one Fandom");
    if (!condition) missing.push("Choose a Display Condition");
    if (!cleanStatus) missing.push("Choose a Cleanliness Status");
    if (photos.length < settings.min_photos_per_zone) missing.push(`Add ${settings.min_photos_per_zone - photos.length} more main photo(s)`);
    if (issues.includes("other") && !otherDetail.trim()) missing.push("Describe the “Other” issue");
    // Standards: if weights are valid, require a score for each manual standard
    if (activeStandards.length && stdWeightsValid) {
      for (const st of activeStandards) {
        if (st.scoring_type === "auto_cleanliness") continue;
        if (standardScores[st.id] === undefined) missing.push(`Score standard: ${st.name}`);
      }
    }
    return missing;
  };

  const handleApprove = () => {
    const miss = missingFields();
    if (miss.length) { toast.error(`Please complete: ${miss.join(" · ")}`); return; }
    const strip = (arr) => (arr || []).map((p) => ({
      id: p.id,
      url: p.url,
      data_url: p.data_url,
      taken_at: p.taken_at,
      captured_at: p.captured_at,
      display_area_id: p.display_area_id,
      display_area_code: p.display_area_code,
      display_area_name: p.display_area_name,
      display_area_code_snapshot: p.display_area_code_snapshot,
      display_area_name_snapshot: p.display_area_name_snapshot,
      zone_name_snapshot: p.zone_name_snapshot,
      store_name_snapshot: p.store_name_snapshot,
    }));
    const strippedIssuePhotos = Object.fromEntries(Object.entries(issuePhotos).map(([k, v]) => [k, strip(v)]));
    onSave({
      fandom_ids: fandomIds, condition, checklist,
      issues, other_issue_detail: otherDetail,
      comments, action_taken: actionTaken, photos: strip(photos),
      zone_owner_ids: ownerIds,
      cleanliness_status: cleanStatus || null,
      dust_condition: dust || null,
      cleanliness_checklist: cleanCk,
      cleanliness_notes: cleanNotes,
      issue_photos: strippedIssuePhotos,
      standard_scores: standardScores,
    });
  };

  const canApprove = missingFields().length === 0;

  return (
    <>
      {isCorrection && (
        <div className="hos-card p-3 border border-[color:var(--warning)] bg-[color:var(--warning)]/10 text-sm">
          <div className="flex items-center gap-2 text-[color:var(--warning)] font-semibold mb-1">
            <AlertTriangle size={14}/> Correction Requested
          </div>
          {zone.correction_reason}
          {(zone.photos || []).length > 0 && (
            <>
              <div className="label-caps mt-2 mb-1">Original photos (kept as history)</div>
              <PhotoRow photos={zone.photos || []}/>
            </>
          )}
        </div>
      )}

      <div className="hos-card p-4 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-caps">{zone.zone_name || `Zone ${zoneIdx + 1}`} · {zoneIdx + 1} of {totalZones}</div>
            <div className="font-heading text-2xl">{zone.zone_name}</div>
            {zone.floor && <div className="text-xs text-[color:var(--text-secondary)]">Floor: {zone.floor}</div>}
          </div>
        </div>

        {totalZones > 1 && (
          <div className="flex gap-1 overflow-x-auto pb-1">
            {zones.map((z, i) => (
              <button
                key={z.zone_id}
                onClick={() => onJump(i)}
                title={z.zone_name}
                className={`shrink-0 px-2 py-1 rounded text-[10px] border max-w-[110px] truncate ${
                  i === zoneIdx ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]"
                  : ["completed", "corrected"].includes(z.status) ? "border-emerald-400 text-emerald-400"
                  : z.status === "correction_required" ? "border-[color:var(--warning)] text-[color:var(--warning)]"
                  : "border-[color:var(--border)] text-[color:var(--text-secondary)]"
                }`}
                data-testid={`zone-nav-${i}`}
              >
                {z.zone_name || `Zone ${i + 1}`}
              </button>
            ))}
          </div>
        )}

        {/* 1) Zone Owners (daily) */}
        <ZoneOwnerPicker
          employees={employeePool}
          selected={ownerIds}
          onToggle={toggleOwner}
          required={settings.require_zone_owner}
        />

        {/* 2) Fandoms */}
        <div>
          <label className="label-caps">Fandom(s) in this zone</label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {availableFandoms.map((f) => {
              const active = fandomIds.includes(f.id);
              return (
                <button key={f.id} onClick={() => toggleFandom(f.id)}
                        className={`p-2 rounded-md border text-xs text-left ${active ? "bg-[color:var(--gold)]/15 border-[color:var(--gold)]" : "border-[color:var(--border)]"}`}
                        data-testid={`fandom-${f.id}`}>
                  {f.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* 3) Display condition */}
        <div>
          <label className="label-caps">Display Condition</label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {CONDITIONS.map((c) => {
              const active = condition === c.key;
              const cls = c.color === "emerald" ? "border-emerald-400 text-emerald-300"
                : c.color === "gold" ? "border-[color:var(--gold)] text-[color:var(--gold)]"
                : c.color === "warning" ? "border-[color:var(--warning)] text-[color:var(--warning)]"
                : "border-[color:var(--error)] text-[color:var(--error)]";
              return (
                <button key={c.key} onClick={() => setCondition(c.key)}
                        className={`p-3 rounded-md border ${active ? cls + " bg-white/5" : "border-[color:var(--border)] text-[color:var(--text-secondary)]"}`}
                        data-testid={`condition-${c.key}`}>
                  {c.label}
                </button>
              );
            })}
          </div>
          {condition === "critical" && (
            <div className="text-[11px] text-[color:var(--error)] mt-1">GM will be notified immediately when saved.</div>
          )}
        </div>

        {/* 4) Stock/Merchandising checklist */}
        <div>
          <label className="label-caps">Stock &amp; Merchandising Checklist</label>
          <div className="space-y-2 mt-1">
            {CHECKLIST_ITEMS.map((it) => (
              <div key={it.key} className="flex items-center justify-between text-sm p-2 rounded-md border border-[color:var(--border)]">
                <span>{it.label}</span>
                <div className="flex gap-1">
                  <button onClick={() => setCk(it.key, true)}
                          className={`px-3 py-1 rounded text-xs ${checklist[it.key] === true ? "bg-emerald-500/20 border border-emerald-400 text-emerald-300" : "border border-[color:var(--border)] text-[color:var(--text-secondary)]"}`}
                          data-testid={`ck-${it.key}-yes`}>Yes</button>
                  <button onClick={() => setCk(it.key, false)}
                          className={`px-3 py-1 rounded text-xs ${checklist[it.key] === false ? "bg-red-500/20 border border-red-400 text-red-300" : "border border-[color:var(--border)] text-[color:var(--text-secondary)]"}`}
                          data-testid={`ck-${it.key}-no`}>No</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 5) Cleanliness */}
        <CleanlinessSection
          status={cleanStatus} setStatus={setCleanStatus}
          dust={dust} setDust={setDust}
          checklist={cleanCk} setChecklistItem={setCleanItem}
          notes={cleanNotes} setNotes={setCleanNotes}
        />

        {/* 5b) Display Standards weighted scoring */}
        <StandardsSection
          standards={activeStandards}
          scores={standardScores}
          setScore={(sid, v) => setStandardScores({ ...standardScores, [sid]: v })}
          cleanStatus={cleanStatus}
          totalWeight={stdTotal}
          weightsValid={stdWeightsValid}
          preview={previewScore()}
        />

        {/* 6) Main zone photos */}
        <div>
          <label className="label-caps flex items-center justify-between">
            <span>Main Zone Photos <span className="text-[color:var(--text-secondary)] normal-case">({photos.length}/{settings.max_photos_per_zone}, min {settings.min_photos_per_zone})</span></span>
          </label>
          {displayAreas.length > 0 && (
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <label className="text-xs text-[color:var(--text-secondary)]">Photo area:</label>
              <select className="hos-input py-1 max-w-xs text-sm" value={selectedAreaId} onChange={(e) => setSelectedAreaId(e.target.value)} data-testid="photo-area-select">
                {displayAreas.map((a) => (
                  <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                ))}
              </select>
            </div>
          )}
          {displayAreas.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1" data-testid="area-coverage">
              {areaCoverage.map((c) => (
                <button key={c.area.id} type="button" onClick={() => setSelectedAreaId(c.area.id)}
                        className={`text-[10px] px-2 py-1 rounded-full border ${c.count === 0 ? "border-[color:var(--warning)]/40 text-[color:var(--warning)]" : "border-emerald-400/40 text-emerald-300"} ${selectedAreaId === c.area.id ? "ring-1 ring-[color:var(--gold)]" : ""}`}
                        data-testid={`area-chip-${c.area.code}`}>
                  {c.area.code} {c.count === 0 ? "⚠" : `✓ ${c.count}`}
                </button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 mt-2">
            {photos.map((p, i) => {
              const cap = formatPhotoCaption(p);
              return (
                <div key={i} data-testid={`photo-tile-${i}`}>
                  <div className="relative aspect-square rounded overflow-hidden border border-[color:var(--border)]">
                    <img src={p.preview || p.data_url} alt="" className="w-full h-full object-cover"/>
                    <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-black/70 text-[color:var(--error)] rounded p-1" data-testid={`photo-remove-${i}`}>
                      <Trash2 size={12}/>
                    </button>
                  </div>
                  {(cap.primary || cap.secondary) && (
                    <div className="mt-1 text-[10px] leading-tight" data-testid={`photo-caption-${i}`}>
                      <div className="text-[color:var(--gold)] font-semibold uppercase truncate">{cap.primary}</div>
                      <div className="text-[color:var(--text-secondary)] truncate">{cap.secondary}</div>
                    </div>
                  )}
                </div>
              );
            })}
            {photos.length < settings.max_photos_per_zone && (
              (displayAreas.length === 0 || selectedAreaId) ? (
                <label className="aspect-square rounded border-2 border-dashed border-[color:var(--border)] flex flex-col items-center justify-center text-[color:var(--text-secondary)] cursor-pointer hover:border-[color:var(--gold)]" data-testid="take-photo-btn">
                  <Camera size={22}/>
                  <span className="text-[10px] mt-1 text-center px-1">{uploading ? "Compressing…" : "Take / Add Photo"}</span>
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={addPhoto} multiple disabled={uploading}/>
                </label>
              ) : (
                <div className="aspect-square rounded border-2 border-dashed border-[color:var(--warning)]/40 flex flex-col items-center justify-center text-[color:var(--warning)] px-2 text-center" data-testid="take-photo-blocked">
                  <Camera size={22}/>
                  <span className="text-[10px] mt-1">Select a Display Area first</span>
                </div>
              )
            )}
          </div>
        </div>

        {/* 7) Issues + optional per-issue photos */}
        <div>
          <label className="label-caps">Issues Found (optional)</label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {ISSUE_TYPES.map((iss) => {
              const active = issues.includes(iss.key);
              return (
                <button key={iss.key} onClick={() => toggleIssue(iss.key)}
                        className={`p-2 rounded-md border text-xs text-left ${active ? "border-[color:var(--warning)] bg-[color:var(--warning)]/10 text-[color:var(--warning)]" : "border-[color:var(--border)] text-[color:var(--text-secondary)]"}`}
                        data-testid={`issue-${iss.key}`}>
                  {iss.label}
                </button>
              );
            })}
          </div>
          {issues.includes("other") && (
            <input className="hos-input mt-2" placeholder="Describe the other issue…" value={otherDetail} onChange={(e) => setOtherDetail(e.target.value)} data-testid="other-detail"/>
          )}

          {issues.length > 0 && (
            <div className="mt-3 space-y-3">
              {issues.map((k) => (
                <IssuePhotoBlock
                  key={k}
                  issueKey={k}
                  label={ISSUE_LABEL[k] || k}
                  photos={issuePhotos[k] || []}
                  onAdd={(files) => addIssuePhotos(k, files)}
                  onRemove={(i) => removeIssuePhoto(k, i)}
                  uploading={uploading}
                  max={settings.max_photos_per_zone}
                />
              ))}
            </div>
          )}
        </div>

        {/* 8) Comments / actions */}
        <div>
          <label className="label-caps">Comments</label>
          <textarea className="hos-input min-h-[70px]" value={comments} onChange={(e) => setComments(e.target.value)} data-testid="zone-comments"/>
        </div>
        <div>
          <label className="label-caps">Action Taken (optional)</label>
          <input className="hos-input" value={actionTaken} onChange={(e) => setActionTaken(e.target.value)} data-testid="zone-action"/>
        </div>

        <button
          className="hos-btn-primary w-full text-lg py-3 flex items-center justify-center gap-2"
          disabled={busy}
          onClick={handleApprove}
          data-testid="approve-zone-btn"
        >
          <ChevronRight size={20}/> {busy ? "Saving…" : (isCorrection ? "Resubmit Zone" : "Complete Zone")}
        </button>
        {!canApprove && (
          <div className="text-[11px] text-[color:var(--warning)] text-center">
            Missing: {missingFields().join(" · ")}
          </div>
        )}
      </div>
    </>
  );
}

function ZoneOwnerPicker({ employees, selected, onToggle, required }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return employees.slice(0, 30);
    return employees.filter((u) =>
      (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s) || (u.employee_id || "").toLowerCase().includes(s)
    ).slice(0, 30);
  }, [q, employees]);
  const nameOf = (uid) => employees.find((u) => u.id === uid)?.name || uid;
  return (
    <div>
      <div className="flex items-center gap-2 label-caps">
        <Users size={12}/> Zone Owner(s) — Today {required && <span className="text-[color:var(--error)]">*</span>}
      </div>
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-1 mt-2" data-testid="zone-owner-chips">
          {selected.map((uid) => (
            <span key={uid} className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[color:var(--gold)]/15 border border-[color:var(--gold)]/40 text-[color:var(--gold)]">
              {nameOf(uid)}
              <button onClick={() => onToggle(uid)} data-testid={`zone-owner-remove-${uid}`}><X size={10}/></button>
            </span>
          ))}
        </div>
      ) : (
        <div className="text-xs text-[color:var(--text-secondary)] mt-1">No Zone Owner selected yet. Zone Owners are chosen fresh for each Display Check.</div>
      )}
      <div className="relative mt-2">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-secondary)]"/>
        <input className="hos-input pl-9" placeholder="Search employees to add…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="zone-owner-search"/>
      </div>
      <div className="mt-2 max-h-48 overflow-y-auto border border-[color:var(--border)] rounded-md">
        {filtered.map((u) => {
          const active = selected.includes(u.id);
          return (
            <button
              key={u.id}
              onClick={() => onToggle(u.id)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between border-b border-[color:var(--border)]/50 last:border-0 ${active ? "bg-[color:var(--gold)]/10" : "hover:bg-[color:var(--bg-surface-2)]"}`}
              data-testid={`zone-owner-opt-${u.id}`}
            >
              <span>
                <span className="font-semibold">{u.name}</span>
                <span className="text-[10px] text-[color:var(--text-secondary)] ml-2 uppercase">{u.role}</span>
              </span>
              {active && <CheckCircle2 size={14} className="text-emerald-400"/>}
            </button>
          );
        })}
        {filtered.length === 0 && <div className="p-3 text-xs text-[color:var(--text-secondary)]">No matching employees.</div>}
      </div>
    </div>
  );
}

function CleanlinessSection({ status, setStatus, dust, setDust, checklist, setChecklistItem, notes, setNotes }) {
  const answered = Object.values(checklist).filter((v) => v === true || v === false);
  const score = answered.length ? Math.round(100 * answered.filter((v) => v === true).length / answered.length) : null;
  return (
    <div className="p-3 rounded-md border border-[color:var(--border)] bg-[color:var(--bg-surface-2)]/50 space-y-3">
      <div className="flex items-center gap-2 label-caps">
        <Broom size={12}/> Zone Cleanliness
        {score !== null && <span className="ml-auto text-[10px] text-[color:var(--gold)] normal-case">Score {score}%</span>}
      </div>
      <div>
        <div className="label-caps text-[10px] mb-1">Cleanliness Status <span className="text-[color:var(--error)]">*</span></div>
        <div className="grid grid-cols-2 gap-2">
          {CLEANLINESS.map((c) => {
            const active = status === c.key;
            const cls = c.color === "emerald" ? "border-emerald-400 text-emerald-300"
              : c.color === "warning" ? "border-[color:var(--warning)] text-[color:var(--warning)]"
              : "border-[color:var(--error)] text-[color:var(--error)]";
            return (
              <button key={c.key} onClick={() => setStatus(c.key)}
                      className={`p-2 rounded-md border text-xs ${active ? cls + " bg-white/5" : "border-[color:var(--border)] text-[color:var(--text-secondary)]"}`}
                      data-testid={`clean-${c.key}`}>
                {c.label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="label-caps text-[10px] mb-1">Dust Condition</div>
        <div className="grid grid-cols-2 gap-2">
          {DUST.map((d) => {
            const active = dust === d.key;
            return (
              <button key={d.key} onClick={() => setDust(d.key)}
                      className={`p-2 rounded-md border text-xs ${active ? "border-[color:var(--gold)] text-[color:var(--gold)] bg-[color:var(--gold)]/5" : "border-[color:var(--border)] text-[color:var(--text-secondary)]"}`}
                      data-testid={`dust-${d.key}`}>
                {d.label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="label-caps text-[10px] mb-1">Cleanliness Checklist</div>
        <div className="space-y-1.5">
          {CLEAN_CHECKLIST.map((it) => (
            <div key={it.key} className="flex items-center justify-between text-sm p-2 rounded border border-[color:var(--border)]">
              <span className="pr-2">{it.label}</span>
              <div className="flex gap-1">
                <button onClick={() => setChecklistItem(it.key, true)} className={`px-2 py-1 rounded text-xs ${checklist[it.key] === true ? "bg-emerald-500/20 border border-emerald-400 text-emerald-300" : "border border-[color:var(--border)] text-[color:var(--text-secondary)]"}`} data-testid={`clean-ck-${it.key}-yes`}>Yes</button>
                <button onClick={() => setChecklistItem(it.key, false)} className={`px-2 py-1 rounded text-xs ${checklist[it.key] === false ? "bg-red-500/20 border border-red-400 text-red-300" : "border border-[color:var(--border)] text-[color:var(--text-secondary)]"}`} data-testid={`clean-ck-${it.key}-no`}>No</button>
                {it.allowNA && (
                  <button onClick={() => setChecklistItem(it.key, null)} className={`px-2 py-1 rounded text-xs ${checklist[it.key] === null ? "border border-[color:var(--gold)] text-[color:var(--gold)]" : "border border-[color:var(--border)] text-[color:var(--text-secondary)]"}`} data-testid={`clean-ck-${it.key}-na`}>N/A</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="label-caps text-[10px] mb-1">Cleanliness Notes (optional)</div>
        <textarea className="hos-input min-h-[60px]" placeholder="e.g. Light dust visible on top shelf" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="clean-notes"/>
      </div>
    </div>
  );
}

const STANDARD_TIERS = [
  { key: 100, label: "Excellent" },
  { key: 80, label: "Good" },
  { key: 50, label: "Needs Attention" },
  { key: 0, label: "Fail" },
];

function StandardsSection({ standards, scores, setScore, cleanStatus, totalWeight, weightsValid, preview }) {
  if (!standards || standards.length === 0) {
    return (
      <div className="p-3 rounded-md border border-dashed border-[color:var(--border)] text-xs text-[color:var(--text-secondary)]">
        No active Display Standards are configured for this Zone/Fandom. Display Score will be unavailable — Admin can add standards under Admin → Display Standards.
      </div>
    );
  }
  const cleanMap = { very_clean: 100, clean: 90, needs_cleaning: 60, dust_present: 55, dirty: 30, critical: 10 };
  return (
    <div className="p-3 rounded-md border border-[color:var(--border)] bg-[color:var(--bg-surface-2)]/50 space-y-3" data-testid="standards-section">
      <div className="flex items-center justify-between label-caps">
        <span>Display Standards (weighted scoring)</span>
        <span className={`normal-case text-[10px] ${weightsValid ? "text-emerald-400" : "text-[color:var(--warning)]"}`}>
          Total {Math.round(totalWeight)}% {weightsValid ? "✓" : "⚠ requires 100%"}
        </span>
      </div>
      {!weightsValid && (
        <div className="text-[11px] text-[color:var(--warning)]">
          Standard weights don't total 100 — score cannot be calculated until Admin fixes the configuration.
        </div>
      )}
      <div className="space-y-2">
        {standards.map((st) => {
          const auto = st.scoring_type === "auto_cleanliness";
          const autoScore = auto ? (cleanMap[cleanStatus] ?? null) : null;
          const current = auto ? autoScore : scores[st.id];
          return (
            <div key={st.id} className="p-2 rounded border border-[color:var(--border)]">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <div className="text-sm font-semibold">{st.name}</div>
                  {st.description && <div className="text-[10px] text-[color:var(--text-secondary)]">{st.description}</div>}
                </div>
                <div className="text-[10px] text-[color:var(--gold)]">Weight {Math.round(st.weight_percentage)}%</div>
              </div>
              {auto ? (
                <div className="text-[11px] text-[color:var(--text-secondary)]">Auto-scored from Cleanliness Status: {autoScore === null ? "—" : `${autoScore}%`}</div>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {STANDARD_TIERS.map((t) => (
                    <button key={t.key} onClick={() => setScore(st.id, t.key)}
                            className={`px-2 py-1 rounded text-[11px] border ${current === t.key ? "border-[color:var(--gold)] text-[color:var(--gold)] bg-[color:var(--gold)]/10" : "border-[color:var(--border)] text-[color:var(--text-secondary)]"}`}
                            data-testid={`std-${st.id}-${t.key}`}>
                      {t.label} · {t.key}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {preview !== null && (
        <div className="text-right text-xs">
          Preview Display Score: <span className="text-[color:var(--gold)] font-heading text-lg">{preview}</span> / 100
        </div>
      )}
    </div>
  );
}


function IssuePhotoBlock({ issueKey, label, photos, onAdd, onRemove, uploading, max }) {
  const inputRef = useRef();
  return (
    <div className="p-2 rounded-md border border-[color:var(--warning)]/30 bg-[color:var(--warning)]/5">
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-semibold text-[color:var(--warning)]">{label}</div>
        <div className="text-[10px] text-[color:var(--text-secondary)]">{photos.length}/{max} photos (optional)</div>
      </div>
      <div className="grid grid-cols-4 gap-1">
        {photos.map((p, i) => {
          const cap = formatPhotoCaption(p);
          return (
            <div key={i}>
              <div className="relative aspect-square rounded overflow-hidden border border-[color:var(--border)]">
                <img src={p.preview || p.data_url} alt="" className="w-full h-full object-cover"/>
                <button onClick={() => onRemove(i)} className="absolute top-0.5 right-0.5 bg-black/70 text-[color:var(--error)] rounded p-0.5" data-testid={`issue-photo-remove-${issueKey}-${i}`}>
                  <Trash2 size={10}/>
                </button>
              </div>
              {(cap.primary || cap.when) && (
                <div className="mt-0.5 text-[9px] leading-tight">
                  <div className="text-[color:var(--warning)] font-semibold uppercase truncate">{cap.primary}</div>
                  <div className="text-[color:var(--text-secondary)] truncate">{cap.when}</div>
                </div>
              )}
            </div>
          );
        })}
        {photos.length < max && (
          <label className="aspect-square rounded border-2 border-dashed border-[color:var(--warning)]/40 flex flex-col items-center justify-center text-[color:var(--warning)] cursor-pointer" data-testid={`add-issue-photo-${issueKey}`}>
            <Camera size={16}/>
            <span className="text-[9px] mt-0.5">{uploading ? "…" : "Add"}</span>
            <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" multiple
                   onChange={(e) => { onAdd(Array.from(e.target.files || [])); e.target.value = ""; }} disabled={uploading}/>
          </label>
        )}
      </div>
    </div>
  );
}

function PhotoRow({ photos }) {
  if (!photos.length) return null;
  return (
    <div className="grid grid-cols-4 gap-1">
      {photos.map((p) => (
        <AuthImage key={p.id} photo={p} className="aspect-square rounded object-cover border border-[color:var(--border)] w-full"/>
      ))}
    </div>
  );
}

function SummaryView({ dc, onCancel, onSubmit, submitting }) {
  const totalPhotos = (dc.zones || []).reduce((n, z) => n + (z.photos || []).length, 0);
  const totalIssues = (dc.zones || []).reduce((n, z) => n + (z.issues || []).length, 0);
  const totalIssuePhotos = (dc.zones || []).reduce((n, z) => {
    const ip = z.issue_photos || {}; return n + Object.values(ip).reduce((a, arr) => a + (arr || []).length, 0);
  }, 0);
  const byCondition = (dc.zones || []).reduce((m, z) => { m[z.condition] = (m[z.condition] || 0) + 1; return m; }, {});
  const cleanBad = (dc.zones || []).filter((z) => ["needs_cleaning", "dust_present", "dirty", "critical"].includes(z.cleanliness_status)).length;
  const dusty = (dc.zones || []).filter((z) => ["light", "moderate", "heavy"].includes(z.dust_condition)).length;
  const ownersSet = new Set();
  (dc.zones || []).forEach((z) => (z.zone_owner_ids || []).forEach((id) => ownersSet.add(id)));
  return (
    <div className="hos-card p-4 space-y-4" data-testid="dc-summary">
      <div className="font-heading text-2xl">Summary</div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><div className="label-caps">Store</div>{dc.store_name}</div>
        <div><div className="label-caps">Manager</div>{dc.manager_name}</div>
        <div><div className="label-caps">Zones</div>{dc.zones.length}</div>
        <div><div className="label-caps">Main Photos</div>{totalPhotos}</div>
        <div><div className="label-caps">Issues</div>{totalIssues}</div>
        <div><div className="label-caps">Issue Photos</div>{totalIssuePhotos}</div>
        <div><div className="label-caps">Needs Cleaning</div>{cleanBad}</div>
        <div><div className="label-caps">Zones w/ Dust</div>{dusty}</div>
        <div><div className="label-caps">Zone Owners (unique)</div>{ownersSet.size}</div>
        <div><div className="label-caps">Critical</div>{byCondition.critical || 0}</div>
      </div>
      <div className="flex flex-col gap-2">
        <button className="hos-btn-primary flex items-center justify-center gap-2" disabled={submitting} onClick={onSubmit} data-testid="dc-submit-btn">
          <Send size={16}/> {submitting ? "Submitting…" : "Submit Display Check"}
        </button>
        <button className="hos-btn-outline" onClick={onCancel} disabled={submitting}>Back to zones</button>
      </div>
    </div>
  );
}
